import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import mapboxgl, { Map } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
  DEFAULT_MAP_MIN_ZOOM,
  DEFAULT_MAP_MAX_ZOOM,
  DEFAULT_MAP_STYLE,
  MAP_INTERACTION_IDS,
  MAP_THRESHOLDS,
} from '../constants/mapConstants';
import { ERROR_MESSAGES as ERROR_CONSTANTS } from '../constants/errorConstants';

interface MapComponentProps {
  /** Initial center coordinates [longitude, latitude] */
  center?: [number, number];
  /** Initial zoom level */
  zoom?: number;
  /** Minimum zoom level */
  minZoom?: number;
  /** Maximum zoom level */
  maxZoom?: number;
  /** Map style URL or style ID */
  style?: string;
  /** Height of the map container */
  height?: string | number;
  /** Width of the map container */
  width?: string | number;
  /** Callback when map is loaded */
  onMapLoad?: (map: Map) => void;
  /** Callback when map center changes */
  onCenterChange?: (center: [number, number]) => void;
  /** Callback when map zoom changes */
  onZoomChange?: (zoom: number) => void;
  /** Callback when map is clicked - receives [longitude, latitude] */
  onMapClick?: (coordinates: [number, number]) => void;
}

const MapComponent = ({
  center = DEFAULT_MAP_CENTER,
  zoom = DEFAULT_MAP_ZOOM,
  minZoom = DEFAULT_MAP_MIN_ZOOM,
  maxZoom = DEFAULT_MAP_MAX_ZOOM,
  style = DEFAULT_MAP_STYLE,
  height = '100%',
  width = '100%',
  onMapLoad,
  onCenterChange,
  onZoomChange,
  onMapClick,
}: MapComponentProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const internalMapRef = useRef<Map | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use refs to store callbacks so they don't need to be in dependency array
  const onMapLoadRef = useRef(onMapLoad);
  const onCenterChangeRef = useRef(onCenterChange);
  const onZoomChangeRef = useRef(onZoomChange);
  const onMapClickRef = useRef(onMapClick);

  // Update refs when callbacks change
  useEffect(() => {
    onMapLoadRef.current = onMapLoad;
    onCenterChangeRef.current = onCenterChange;
    onZoomChangeRef.current = onZoomChange;
    onMapClickRef.current = onMapClick;
  }, [onMapLoad, onCenterChange, onZoomChange, onMapClick]);

  // Use refs for initial map configuration props
  // These are only used during map initialization, which happens once
  const initialStyleRef = useRef(style);
  const initialCenterRef = useRef(center);
  const initialZoomRef = useRef(zoom);
  const initialMinZoomRef = useRef(minZoom);
  const initialMaxZoomRef = useRef(maxZoom);

  // Update refs when props change (for potential re-initialization if needed)
  useEffect(() => {
    initialStyleRef.current = style;
    initialCenterRef.current = center;
    initialZoomRef.current = zoom;
    initialMinZoomRef.current = minZoom;
    initialMaxZoomRef.current = maxZoom;
  }, [style, center, zoom, minZoom, maxZoom]);

  useEffect(() => {
    const config = window.runtimeConfig || {};
    const accessToken = config.MAPBOX_KEY;

    if (!accessToken) {
      setError(ERROR_CONSTANTS.MAPBOX_TOKEN_NOT_CONFIGURED);
      return;
    }

    mapboxgl.accessToken = accessToken;

    if (mapContainerRef.current && !internalMapRef.current) {
      try {
        const mapInstance = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: initialStyleRef.current,
          center: initialCenterRef.current,
          zoom: initialZoomRef.current,
          minZoom: initialMinZoomRef.current,
          maxZoom: initialMaxZoomRef.current,
          attributionControl: true,
          scrollZoom: true,
        });

        internalMapRef.current = mapInstance;

        mapInstance.on('load', () => {
          setIsMapLoaded(true);
          if (onMapLoadRef.current) {
            onMapLoadRef.current(mapInstance);
          }
        });

        mapInstance.on('moveend', () => {
          if (onCenterChangeRef.current) {
            const center = mapInstance.getCenter();
            onCenterChangeRef.current([center.lng, center.lat]);
          }
        });

        mapInstance.on('zoomend', () => {
          if (onZoomChangeRef.current) {
            onZoomChangeRef.current(mapInstance.getZoom());
          }
        });

        // Use addInteraction for map background clicks (when no features are clicked)
        mapInstance.addInteraction(MAP_INTERACTION_IDS.MAP_BACKGROUND_CLICK, {
          type: 'click',
          handler: ({ lngLat }) => {
            if (onMapClickRef.current) {
              onMapClickRef.current([lngLat.lng, lngLat.lat]);
            }
          },
        });

        mapInstance.on('error', (e) => {
          console.error('Mapbox error:', e);
          setError(
            `${ERROR_CONSTANTS.MAP_ERROR} ${e.error?.message || 'Unknown error'}`
          );
        });
      } catch (err) {
        console.error('Error initializing map:', err);
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(`${ERROR_CONSTANTS.MAP_INITIALIZATION_FAILED} ${message}`);
      }
    }

    return () => {
      if (internalMapRef.current) {
        try {
          internalMapRef.current.removeInteraction(
            MAP_INTERACTION_IDS.MAP_BACKGROUND_CLICK
          );
        } catch {
          // Ignore errors during cleanup - interaction may not exist
        }
        internalMapRef.current.remove();
        internalMapRef.current = null;
        setIsMapLoaded(false);
      }
    };
  }, []); // Empty dependency array is intentional - map is initialized once

  useEffect(() => {
    if (internalMapRef.current && isMapLoaded) {
      const currentCenter = internalMapRef.current.getCenter();
      const [lng, lat] = center;

      const threshold = MAP_THRESHOLDS.CENTER_CHANGE_THRESHOLD;
      const lngDiff = Math.abs(currentCenter.lng - lng);
      const latDiff = Math.abs(currentCenter.lat - lat);

      if (lngDiff > threshold || latDiff > threshold) {
        internalMapRef.current.setCenter(center);
      }
    }
  }, [center, isMapLoaded]);

  useEffect(() => {
    if (internalMapRef.current && isMapLoaded) {
      const currentZoom = internalMapRef.current.getZoom();

      if (Math.abs(currentZoom - zoom) > MAP_THRESHOLDS.ZOOM_CHANGE_THRESHOLD) {
        internalMapRef.current.setZoom(zoom);
      }
    }
  }, [zoom, isMapLoaded]);

  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container || !isMapLoaded || !internalMapRef.current) return;

    const preventScroll = (e: WheelEvent) => {
      // Prevent default page scrolling
      e.preventDefault();
    };

    container.addEventListener('wheel', preventScroll, { passive: false });

    return () => {
      container.removeEventListener('wheel', preventScroll);
    };
  }, [isMapLoaded]);

  if (error) {
    return (
      <Box
        sx={{
          height: height,
          width: width,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'error.light',
          color: 'error.main',
          p: 2,
          textAlign: 'center',
        }}
      >
        {error}
      </Box>
    );
  }

  return (
    <Box
      ref={mapContainerRef}
      sx={{
        height: height,
        width: width,
        position: 'relative',
        overflow: 'hidden',
      }}
    />
  );
};

export default MapComponent;
